import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { MultiSelect } from "primereact/multiselect";
import { ProgressSpinner } from "primereact/progressspinner";
import { TabPanel, TabView } from "primereact/tabview";
import { useAppContext } from "../contexts/AppContext";
import {
  ReportComparatorService,
  ReportComparisonSummary,
  ReportSheetEntry,
} from "../services/ReportComparatorService";
import "../styles/pages/ReportComparatorPage.scss";

const isValidExcelFile = (file: File | null) =>
  !!file && file.name.toLowerCase().endsWith(".xlsx");

const formatCellValue = (value: string | number | boolean | null | undefined) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
};

const ReportComparatorPage: React.FC = () => {
  const { setPageTitle } = useAppContext();
  const [file1, setFile1] = useState<File | null>(null);
  const [file2, setFile2] = useState<File | null>(null);
  const [file1Error, setFile1Error] = useState<string>("");
  const [file2Error, setFile2Error] = useState<string>("");
  const [summary, setSummary] = useState<ReportComparisonSummary>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  useEffect(() => {
    setPageTitle({ title: "Report Comparator" });
  }, [setPageTitle]);

  const handleFileSelection = (nextFile: File | null, side: "file1" | "file2") => {
    if (!nextFile) {
      if (side === "file1") {
        setFile1(null);
        setFile1Error("Please select a file.");
      } else {
        setFile2(null);
        setFile2Error("Please select a file.");
      }
      return;
    }

    const isValid = isValidExcelFile(nextFile);
    const message = isValid ? "" : "Only .xlsx files are allowed.";

    if (side === "file1") {
      setFile1(nextFile);
      setFile1Error(message);
    } else {
      setFile2(nextFile);
      setFile2Error(message);
    }
  };

  const compareEnabled = !!file1 && !!file2 && !file1Error && !file2Error;

  const sheetNames = useMemo(() => Object.keys(summary), [summary]);

  const columnFilterOptions = useMemo(() => {
    const optionsByColumn: Record<string, Array<{ label: string; value: string }>> = {};

    Object.values(summary).forEach((rows) => {
      rows.forEach((row) => {
        Object.entries(row).forEach(([column, value]) => {
          if (column === "File1Value" || column === "File2Value") {
            return;
          }

          const normalizedValue = formatCellValue(value);
          if (!normalizedValue) {
            return;
          }

          const existing = optionsByColumn[column] ?? [];
          const alreadyExists = existing.some(
            (option) => option.value === normalizedValue,
          );

          if (!alreadyExists) {
            existing.push({ label: normalizedValue, value: normalizedValue });
            optionsByColumn[column] = existing;
          }
        });
      });
    });

    Object.keys(optionsByColumn).forEach((column) => {
      const columnOptions = optionsByColumn[column] ?? [];
      optionsByColumn[column] = [...columnOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    });

    return optionsByColumn;
  }, [summary]);

  const runComparison = async () => {
    if (!compareEnabled || !file1 || !file2) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const result = await ReportComparatorService.compareReports(file1, file2);
      setSummary(result ?? {});
      setActiveTabIndex(0);
    } catch (comparisonError) {
      const message =
        comparisonError instanceof Error
          ? comparisonError.message
          : "Comparison failed. Please verify both files and try again.";
      setError(message);
      setSummary({});
    } finally {
      setIsLoading(false);
    }
  };

  const renderFileInput = (
    side: "file1" | "file2",
    label: string,
    selectedFile: File | null,
    validationError: string,
    onChange: (file: File | null) => void,
  ) => {
    const inputId = `${side}-input`;
    return (
      <div className="file-upload-box">
        <label htmlFor={inputId}>{label}</label>
        <input
          id={inputId}
          type="file"
          accept=".xlsx"
          onChange={(event) => {
            const nextFile = event.target.files?.[0] ?? null;
            onChange(nextFile);
            event.target.value = "";
          }}
          className="file-upload-input"
        />
        <div
          onClick={() => document.getElementById(inputId)?.click()}
          className={`file-upload-dropzone ${validationError ? "error" : ""}`}
        >
          <i className="pi pi-upload file-upload-icon" />
          <span>{selectedFile ? selectedFile.name : `Choose ${label}`}</span>
        </div>
        {validationError && <small className="file-upload-error">{validationError}</small>}
      </div>
    );
  };

  const renderTextSearchFilter = (options: {
    value?: unknown;
    filterApplyCallback?: (value: unknown) => void;
  }) => {
    const searchValue = typeof options.value === "string" ? options.value : "";

    return (
      <div className="report-comparator-filter-row report-comparator-search-only-row">
        <InputText
          value={searchValue}
          placeholder="Search"
          className="report-comparator-filter-search"
          onChange={(event) => options.filterApplyCallback?.(event.target.value)}
        />
      </div>
    );
  };

  const renderChangeColumnFilter = (options: {
    value?: unknown;
    filterApplyCallback?: (value: unknown) => void;
  }) => {
    const dropdownOptions = columnFilterOptions["Change Column"] ?? [];
    const currentValue = Array.isArray(options.value)
      ? options.value as string[]
      : typeof options.value === "string"
        ? [options.value]
        : [];

    return (
      <div className="report-comparator-filter-row report-comparator-change-column-row">
        <MultiSelect
          value={currentValue}
          options={dropdownOptions}
          onChange={(event) => options.filterApplyCallback?.(event.value ?? [])}
          placeholder="Select"
          className="report-comparator-column-filter"
          panelClassName="report-comparator-column-filter-panel"
          display="chip"
          maxSelectedLabels={1}
          showClear={false}
        />
      </div>
    );
  };

  return (
    <div className="report-comparator-page">
      <div className="report-comparator-controls">
        {error && (
          <div className="report-comparator-error">
            <Message severity="error" text={error} />
          </div>
        )}

        <div className="report-comparator-upload-row">
          {renderFileInput(
            "file1",
            "File 1",
            file1,
            file1Error,
            (nextFile) => handleFileSelection(nextFile, "file1"),
          )}
          {renderFileInput(
            "file2",
            "File 2",
            file2,
            file2Error,
            (nextFile) => handleFileSelection(nextFile, "file2"),
          )}
          <Button
            label="Compare Files"
            icon="pi pi-check"
            onClick={runComparison}
            disabled={!compareEnabled || isLoading}
            loading={isLoading}
            className="report-comparator-button"
          />
        </div>
      </div>

      {isLoading && (
        <div className="report-comparator-spinner">
          <ProgressSpinner />
        </div>
      )}

      {!isLoading && sheetNames.length > 0 && (
        <div className="report-comparator-results" aria-live="polite">
          <div className="main-results-section">
            <div className="results-tabs-with-actions">
              <TabView
                activeIndex={activeTabIndex}
                onTabChange={(event) => setActiveTabIndex(event.index)}
                className="main-results-tabs"
              >
                {sheetNames.map((sheetName) => {
                  const rowsForSheet = summary[sheetName] ?? [];
                  const firstRow = rowsForSheet[0] ?? {};

                  return (
                    <TabPanel
                      key={sheetName}
                      header={
                        <span className="tab-header-with-count">
                          {sheetName}
                          {rowsForSheet.length > 0 && (
                            <span className="tab-count-badge">{rowsForSheet.length}</span>
                          )}
                        </span>
                      }
                    >
                      <DataTable
                        value={rowsForSheet}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        stripedRows
                        filterDisplay="row"
                        emptyMessage="No differences found for this sheet."
                        rowClassName={(row) => (row["Change Column"] === "NEW" ? "new-row" : "")}
                        className="data-table report-comparator-table"
                      >
                        {Object.keys(firstRow).map((column) => {
                          const normalizedColumn = column.replace(/\s+/g, "");
                          const isFileValueColumn =
                            normalizedColumn === "File1Value" ||
                            normalizedColumn === "File2Value" ||
                            column === "File1 Value" ||
                            column === "File2 Value";
                          const isChangeColumn =
                            normalizedColumn === "ChangeColumn" ||
                            column === "Change Column";

                          return (
                            <Column
                              key={column}
                              field={column}
                              header={column}
                              filter={!isFileValueColumn}
                              filterMatchMode={
                                isFileValueColumn
                                  ? undefined
                                  : isChangeColumn
                                    ? "in"
                                    : "contains"
                              }
                              showFilterMenu={false}
                              filterElement={
                                isFileValueColumn
                                  ? undefined
                                  : (options) =>
                                      isChangeColumn
                                        ? renderChangeColumnFilter(options)
                                        : renderTextSearchFilter(options)
                              }
                              body={(rowData: ReportSheetEntry) =>
                                formatCellValue(rowData[column as keyof ReportSheetEntry])
                              }
                            />
                          );
                        })}
                      </DataTable>
                    </TabPanel>
                  );
                })}
              </TabView>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportComparatorPage;
